import React, { createContext, useContext, useEffect, useState } from "react";
import { api, root, bootstrapCsrf, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

// GET /api/user returns a Laravel resource wrapped as { data: {...} }; the login
// response nests the same user un-wrapped under `user`. Normalize both.
function unwrapUser(payload) {
  return payload?.data ?? payload;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore the session from the Sanctum cookie on boot. No token to check —
  // the browser holds the cookie, so just ask the backend who we are.
  useEffect(() => {
    api
      .get("/user")
      .then((res) => setUser(unwrapUser(res.data)))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  async function login(email, password) {
    await bootstrapCsrf();
    const res = await root.post("/login", { email, password });
    const u = res.data.user;
    setUser(u);
    return u;
  }

  async function logout() {
    try {
      await root.post("/logout");
    } catch (_e) {}
    setUser(null);
  }

  // Self-service password change. On success the backend stamps
  // password_changed_at, so the returned user has must_change_password=false —
  // which clears the forced-reset gate in <App>.
  async function changePassword({ current_password, password, password_confirmation }) {
    await bootstrapCsrf();
    const res = await api.post("/user/password", {
      current_password,
      password,
      password_confirmation,
    });
    const u = unwrapUser(res.data.user ?? res.data);
    setUser(u);
    return u;
  }

  // Permission gate. The backend UserResource ships a flat `permissions` name
  // list (Spatie getAllPermissions). Screens still gate on `user.role_code`
  // today; `can()` lets us migrate to permission checks one screen at a time.
  function can(permission) {
    return Boolean(user?.permissions?.includes(permission));
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, changePassword, can, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
